// src/data/demoClients.ts
// ─────────────────────────────────────────────────────────────────────────────
// Demo client data for testing. Both clients have ALL ClientData fields.
// Built from the client profiles in FFM Project State v10.
// ─────────────────────────────────────────────────────────────────────────────

import { ClientData, BLANK_CLIENT } from '../types/ClientData';

const EMPTY_VOYANT = {
  retirementAge: null,
  potAtRetirement: null,
  annualIncomeAchievable: null,
  voyantLumpSumNeeded: null,
  voyantAnnualSavingsNeeded: null,
  capturedDate: '',
  voyantAssetsAtTarget: null,
  voyantLiabilitiesAtTarget: null,
  voyantNetWorthAtTarget: null,
};

// ─── Sarah Mitchell — Simple case (basic rate) ──────────────────────────────
// SM Creative Consulting Ltd. Age 41. Single. Freedom age: 60.
// Salary £12,570, Dividends £35,000. Total: £47,570. All basic rate.
// Story: doing nothing deliberately wrong, just doing nothing deliberate.

export const SARAH_MITCHELL: ClientData = {
  ...BLANK_CLIENT,

  // Session meta
  clientName: 'Sarah Mitchell',
  businessName: 'SM Creative Consulting Ltd',
  adviserName: 'Peter Brooksbank',
  sessionDate: '09/04/2026',
  sessionType: 'new',

  // Xero connection (demo stub — backend serves canned data for this tenantId)
  xeroTenantId: 'demo-sarah-mitchell',
  xeroTenantName: 'SM Creative Consulting Ltd (Demo)',

  // Client profile
  clientAge: 41,
  partnerAge: 0,
  isCouple: false,
  targetFreedomAge: 60,

  // Growth assumptions
  assumptionPensionGrowth: 0.08,
  assumptionIsaGrowth: 0.08,
  assumptionPropertyGrowth: 0.01,
  assumptionCashRate: 0.01,

  // Income — all basic rate
  salary: 12570,
  dividends: 35000,
  rentalIncomeNet: 0,
  savingsInterest: 0,
  otherDividends: 0,
  payeIncome: 0,
  expensesReimbursement: 0,
  taxPaymentMethod: 'monthly',

  // Monthly position — essentials
  essentialsUtilities: 150,
  essentialsFood: 350,
  essentialsTravel: 120,

  // Benchmarking
  clientAgeBand: '40-44',

  // Debt — none
  consumerDebts: [{ name: '', balance: 0, apr: 0, monthlyMinimum: 0 }],
  consumerDebtTotal: 0,
  consumerDebtMonthlyPayment: 0,
  consumerDebtHighestAPR: 0,
  consumerDebtLabel: '',

  // Sinking funds — £300/month loosely tracked
  sinkingFundsTotal: 300,
  sinkingFundsDetail: [
    { id: 'sf1', label: 'Car service / MOT', group: 'Transport', monthly: 80 },
    { id: 'sf2', label: 'Holiday', group: 'Lifestyle', monthly: 120 },
    { id: 'sf3', label: 'Home repairs', group: 'Home', monthly: 100 },
  ],

  // Emergency fund — building (2.7 months)
  efAccount1Name: 'Nationwide Flex Saver',
  efAccount1Balance: 3200,
  efAccount1Rate: 4.8,
  efAccount1Monthly: 200,
  efAccount2Name: 'Monzo Easy Access',
  efAccount2Balance: 7500,
  efAccount2Rate: 3.5,
  efAccount2Monthly: 0,
  efTargetMonths: 6,          // variable income director = 6 months

  // Pension — old employer, dormant
  pensionBalance: 31000,
  pensionBalanceDC: 31000,
  pensionProvider: 'Old employer workplace pension',
  pensionMonthly: 0,
  pensionCompanyFunded: 0,
  pensionSteppedSchedule: [],
  pensionAnnualAllowanceUsed: 0,

  // ISA — never opened
  isaBalance: 0,
  investmentsBalance: 0,
  isaContribution: 0,

  // Mortgage — £280,000 @ 3.9% fix to Feb 2028
  propertyValue: 420000,
  mortgageBalance: 280000,
  mortgageRate: 3.9,
  mortgageTermYears: 22,
  mortgageFixEndDate: '02/2028',
  mortgagePayment: 1470,
  mortgageOP: 0,
  mortgageErcFreeAnnual: 28000,

  // Rental — none
  rentalPropertyValue: 0,
  rentalMortgageBalance: 0,
  rentalMortgageType: 'repayment',

  // DLA — none
  dlaBalance: 0,
  dlaRepaymentSchedule: [],

  // Non-EF savings — none
  savingsAccounts: [{ name: '', balance: 0, rate: 0 }],
  otherSavingsBalance: 0,
  otherSavingsLabel: '',
  otherSavingsRate: 0,

  // Freedom Number
  plsaStandard: 'comfortable',
  statePensionAnnual: 11500,
  statePensionAge: 67,
  freedomNumberToday: 0,
  freedomNumberInflated: 0,
  annualRetirementIncomeNeeded: 0,
  gapAmount: 0,

  // Voyant — do-nothing captured for demo
  voyantDoNothing: {
    ...EMPTY_VOYANT,
    retirementAge: 68,
    potAtRetirement: 180000,
    voyantLumpSumNeeded: 320000,
    voyantAnnualSavingsNeeded: 14000,
    capturedDate: '2026-04-01T10:00:00.000Z',
  },
  voyantPostS1: { ...EMPTY_VOYANT },
  voyantPostS2: { ...EMPTY_VOYANT },

  // Voyant S1 scenario — pre-filled for demo
  voyantS1RetirementAge: 63,
  voyantS1PotAtTargetAge: 310000,
  voyantS1LumpSumNeeded: 190000,
  voyantS1AnnualSavingsNeeded: 8000,
  voyantS1Captured: true,

  // Plan values for demo
  planMortgageInterestSaved: 28000,
  planTaxSavedPerYear: 0,       // basic rate — no dividend reduction
  planCumulativeTaxSaved: 0,

  // Running tally values
  totalTaxSavedAnnual: 0,
  totalTaxSavedCumulative: 0,
  totalInterestSaved: 28000,
  totalGapClosed: 0,

  // Plan decisions (pre-populated for demo — normally set by navigating screens)
  planEfMonthlyTopUp: 200,
  planEfCompleteDate: '2027-06-01',
  planEfRedirectTo: 'isa',
  planMortgageOverpayment: 200,
  planMortgagePayoffAge: 58,
  planMortgagePayoffDate: '2043-01-01',
  planMortgageRedirectTo: 'isa',
  planIsaMonthlyContribution: 200,
  planBuilt: true,

  // Levers — none applied
  leversApplied: [],

  // Session close
  standingOrders: [],
  nextActions: ['', '', ''],
  session2Date: '',
  sessionNotes: '',

  // Business (S2) — solo creative consultant, low overheads
  businessRevenueMRR: 9167,         // £110k / 12
  businessCostsMonthly: 0,
  businessBankBalance: 0,
  businessCtProvisionHeld: 0,
  businessVatProvisionHeld: 0,
  businessEFMonths: 3,

  // Session 2 — Business Position (figures align with Xero stub summary)
  businessMonthlyRevenue: 9167,
  businessGrossMarginPct: 95,        // 104k / 110k
  businessFixedMonthlyCosts: 1717,   // (totalOperating + staffCosts) / 12
  businessVariableCostPct: 5,
  businessOperatingBufferBalance: 4000,   // ≈ 2.3 mo → yellow (building)
  businessTaxReserveBalance: 14000,
  businessEstimatedCTLiability: 15500,    // ≈ 90% reserve → green
  businessDebtorDays: 32,                 // yellow — slight room to improve
  businessDLABalance: 0,                  // green (≥ 0)
  businessRevenueConcentration: 'high' as const,  // solo: 2-3 key clients

  gapHQLevers: {
    leadsPerMonth: 0,
    conversionRatePct: 0,
    avgTransactionValue: 0,
    transactionsPerClientPerYear: 0,
    grossMarginPct: 0,
    fixedCostMonthly: 0,
    variableCostPct: 0,
  },

  // Backward-compat
  otherIncome: 0,
  livingCosts: 2200,
  monthlyHMRC: 240,
  efTopUp: 200,
};


// ─── Claire Henderson — Complex case (higher rate) ──────────────────────────
// Henderson Design Ltd. Age 47. Single. Freedom age: 57.
// Salary £12,570, Dividends £67,430, Net rental £14,400. Total: £94,400.
// Higher rate exposure. Approaching taper zone.
// Story: doing the right things without a framework. Not deliberate.

export const CLAIRE_HENDERSON: ClientData = {
  ...BLANK_CLIENT,

  // Session meta
  clientName: 'Claire Henderson',
  businessName: 'Henderson Design Ltd',
  adviserName: 'Peter Brooksbank',
  sessionDate: '09/04/2026',
  sessionType: 'new',

  // Xero connection (demo stub — backend serves canned data for this tenantId)
  xeroTenantId: 'demo-claire-henderson',
  xeroTenantName: 'Henderson Design Ltd (Demo)',

  // Client profile
  clientAge: 47,
  partnerAge: 0,
  isCouple: false,
  targetFreedomAge: 57,

  // Growth assumptions
  assumptionPensionGrowth: 0.08,
  assumptionIsaGrowth: 0.08,
  assumptionPropertyGrowth: 0.01,
  assumptionCashRate: 0.01,

  // Income — HIGHER RATE: total £94,400
  salary: 12570,
  dividends: 67430,
  rentalIncomeNet: 14400,     // net rental income
  savingsInterest: 0,
  otherDividends: 0,
  payeIncome: 0,
  expensesReimbursement: 0,
  taxPaymentMethod: 'monthly',

  // Monthly position — essentials (Brief 13)
  essentialsUtilities: 180,
  essentialsFood: 400,
  essentialsTravel: 150,

  // Benchmarking (Brief 13)
  clientAgeBand: '45-49',

  // RLSA
  rlsaAnnualTarget: 43100,

  // Debt — none
  consumerDebts: [{ name: '', balance: 0, apr: 0, monthlyMinimum: 0 }],
  consumerDebtTotal: 0,
  consumerDebtMonthlyPayment: 0,
  consumerDebtHighestAPR: 0,
  consumerDebtLabel: '',

  // Sinking funds — £350/month
  sinkingFundsTotal: 350,
  sinkingFundsDetail: [
    { id: 'sf1', label: 'Holiday', group: 'Lifestyle' as const, monthly: 150 },
    { id: 'sf2', label: 'Car / MOT', group: 'Transport' as const, monthly: 100 },
    { id: 'sf3', label: 'Home repairs', group: 'Home' as const, monthly: 100 },
  ],

  // Emergency fund — £18,000 across two accounts (above 3 months)
  efAccount1Name: 'Marcus by Goldman Sachs',
  efAccount1Balance: 10000,
  efAccount1Rate: 4.5,
  efAccount1Monthly: 0,
  efAccount2Name: 'Starling Easy Access',
  efAccount2Balance: 8000,
  efAccount2Rate: 3.25,
  efAccount2Monthly: 0,
  efTargetMonths: 6,

  // Pension — £500/month employer started this year
  pensionBalance: 87000,      // existing DC pension pot
  pensionProvider: 'Scottish Widows Workplace',
  pensionMonthly: 0,
  pensionCompanyFunded: 500,
  pensionSteppedSchedule: [
    { fromYear: 2026, monthlyAmount: 500 },
  ],
  pensionAnnualAllowanceUsed: 6000,

  // ISA — S&S ISA
  isaBalance: 24000,
  investmentsBalance: 24000,
  isaContribution: 0,

  // Mortgage — £320,000 @ 4.5%, 22 years remaining
  propertyValue: 485000,
  mortgageBalance: 320000,
  mortgageRate: 4.5,
  mortgageTermYears: 22,
  mortgageFixEndDate: '09/2027',
  mortgagePayment: 2050,
  mortgageOP: 300,            // overpaying £300/month
  mortgageErcFreeAnnual: 32000,

  // Rental property — £180,000 value, £90,000 IO mortgage
  rentalPropertyValue: 180000,
  rentalMortgageBalance: 90000,
  rentalMortgageType: 'interestOnly',

  // DLA — none
  dlaBalance: 0,
  dlaRepaymentSchedule: [],

  // Non-EF savings
  savingsAccounts: [{ name: 'Easy access saver', balance: 14000, rate: 3.8 }],
  otherSavingsBalance: 14000,
  otherSavingsLabel: 'Easy access saver',
  otherSavingsRate: 3.8,

  // Freedom Number
  plsaStandard: 'comfortable',
  statePensionAnnual: 11500,
  statePensionAge: 67,
  freedomNumberToday: 924232,
  freedomNumberInflated: 1183188,
  annualRetirementIncomeNeeded: 30344,
  gapAmount: 989343,

  // Voyant — do-nothing captured for demo
  voyantDoNothing: {
    ...EMPTY_VOYANT,
    retirementAge: 69,
    potAtRetirement: 193845,
    voyantLumpSumNeeded: 380000,
    voyantAnnualSavingsNeeded: 18000,
    capturedDate: '2026-04-01T10:00:00.000Z',
  },
  voyantPostS1: { ...EMPTY_VOYANT },
  voyantPostS2: { ...EMPTY_VOYANT },

  // Voyant S1 scenario — pre-filled for demo
  voyantS1RetirementAge: 64,
  voyantS1PotAtTargetAge: 420000,
  voyantS1LumpSumNeeded: 210000,
  voyantS1AnnualSavingsNeeded: 9600,  // £800/month S2 target
  voyantS1Captured: true,

  // Plan values for demo
  planMortgageInterestSaved: 52016,
  planTaxSavedPerYear: 3432,
  planCumulativeTaxSaved: 34320,

  // Running tally values (shown in tally bar immediately on demo load)
  totalTaxSavedAnnual: 3432,
  totalTaxSavedCumulative: 34320,
  totalInterestSaved: 52016,
  totalGapClosed: 0,

  // Plan decisions (pre-populated for demo — normally set by navigating screens)
  // Brief 49 S2 — pension lever must start at £0 (previously showed £480.716 float)
  planDividendReduction: 0,
  planDividendReductionCommitted: false,
  planPensionFromDividendReduction: 0,
  estimatedCompanyProfit: 85000,
  planEfMonthlyTopUp: 400,
  planEfCompleteDate: '2026-07-01',
  planEfRedirectTo: 'pension',
  planMortgageOverpayment: 300,
  planMortgageAnnualLumpSum: 0,
  planMortgagePayoffAge: 61,
  planMortgagePayoffDate: '2040-01-01',
  planMortgageRedirectTo: 'isa',
  planIsaMonthlyContribution: 0,
  planBuilt: true,

  // Levers — none applied
  leversApplied: [],

  // Session close
  standingOrders: [],
  nextActions: ['', '', ''],
  session2Date: '',
  sessionNotes: '',

  // Business (S2)
  businessRevenueMRR: 14500,
  businessCostsMonthly: 0,
  businessBankBalance: 0,
  businessCtProvisionHeld: 0,
  businessVatProvisionHeld: 0,
  businessEFMonths: 3,

  // Session 2 — Business Position
  businessMonthlyRevenue: 14500,
  businessGrossMarginPct: 72,
  businessFixedMonthlyCosts: 6500,
  businessVariableCostPct: 28,
  businessOperatingBufferBalance: 20000,   // ≈ 3.3 mo of Xero fixed costs → green
  businessTaxReserveBalance: 18000,        // ≈ 61% of CT liability → yellow
  businessEstimatedCTLiability: 29400,     // aligned to Xero netProfit £122k
  businessDebtorDays: 38,
  businessDLABalance: 26000,
  businessRevenueConcentration: 'low' as const,
  businessClientCount: 18,
  businessAvgMonthlyValue: 806,
  businessAvgRetentionMonths: 28,

  // Session 2 — Lever defaults
  leverClientsTargetPct: 0,
  leverAvgValueTargetPct: 0,
  leverRetentionTargetPct: 0,
  leverMarginTargetPct: 0,
  leverFixedCostReductionMonthly: 0,
  leverVariableCostTargetPct: 0,
  targetDebtorDays: 38,
  billingFrequencyImprovement: 0,
  hmrcReserveMonthlySO: 0,
  legitimateExpensesMonthly: 0,
  gapHQLevers: {
    leadsPerMonth: 0,
    conversionRatePct: 0,
    avgTransactionValue: 0,
    transactionsPerClientPerYear: 0,
    grossMarginPct: 0,
    fixedCostMonthly: 0,
    variableCostPct: 0,
  },

  // Couple — Claire is single
  partnerName: '',
  partnerSalary: 0,
  partnerDividends: 0,
  partnerOtherIncome: 0,
  partnerExpensesReimbursement: 0,
  partnerTaxPaymentMethod: 'none' as const,
  partnerMonthlyHMRC: 0,
  partnerPensionBalanceDC: 0,
  partnerHasDBPension: false,
  partnerDBPensionScheme: '',
  partnerDBPensionAccruedAnnual: 0,
  partnerDBPensionNRA: 67,
  partnerEmployerPensionMonthly: 0,
  partnerISACash: 0,
  partnerISAStocksShares: 0,
  partnerISAMonthly: 0,
  partnerOtherInvestments: 0,

  // ISA split — Claire has S&S ISA only
  isaCash: 0,
  isaStocksShares: 24000,

  // Pension — DC only, no DB
  pensionBalanceDC: 87000,
  hasDBPension: false,
  dbPensionScheme: '',
  dbPensionAccruedAnnual: 0,
  dbPensionNRA: 67,

  // Rental — one buy-to-let flat
  rentalPropertyCount: 1,
  rentalEquityTotal: 90000,
  rentalMonthlyGrossIncome: 950,
  rentalMonthlyMortgageCosts: 520,
  rentalMonthlyRunningCosts: 120,

  // No children's costs
  childMonthlyCommittedCosts: 0,
  childCostsEndAge: 0,

  // No EIS/SEIS
  eisSeisBalance: 0,

  intakeNotes: '',
  intakeSubmittedAt: '',

  // Backward-compat
  otherIncome: 14400,
  livingCosts: 3200,
  monthlyHMRC: 0,
  efTopUp: 200,  // £200/month going into savings account
};
